import Blockquote from '@tiptap/extension-blockquote';
import BulletList from '@tiptap/extension-bullet-list';
import Heading from '@tiptap/extension-heading';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';

/*
 * The spacing between a Markdown marker and its text, as stored:
 * "2.       Purpose", "-   item", "#   Title", ">   quote". Often several
 * spaces or a tab (text from Word, or non-breaking spaces from the old
 * editor). It isn't shown, so items don't start with blank space, and it's
 * written back unchanged so saved prompts stay exact. Items created in the
 * editor use a single space.
 */
const markerGap = {
  default: ' ',
  rendered: false,
  // A new item made by pressing Enter gets the normal single space
  keepOnSplit: false,
};

/*
 * Standard Markdown needs a blank line between blocks of different types, or
 * it merges them (a line after a list becomes part of the last item, a table
 * after a list becomes part of it, ...). The editor writes that blank line
 * (see prompt-markdown.ts), except where a stored prompt had none and nothing
 * around it has changed, so a save with no edits doesn't change the prompt.
 *
 * Each block loaded from a stored prompt gets an id, and a block stored with
 * no blank line above it remembers the id of the block it was stored under
 * ("joinedTo"). The stored spacing is kept only while that same block is
 * still directly above it: a new block above it (typed, pasted, a table, a
 * list or quote wrapped around the block above) or a deleted block in between
 * means the blank line is written. Blocks created in the editor have no id.
 */
export const blockSpacing = {
  blockId: { default: null, rendered: false, keepOnSplit: false },
  joinedTo: { default: null, rendered: false, keepOnSplit: false },
};

export const PromptParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // After ">" when the paragraph is a line of a quote
      quoteGap: markerGap,
      ...blockSpacing,
    };
  },
});

export const PromptHeading = Heading.extend({
  addAttributes() {
    return { ...this.parent?.(), markerGap, ...blockSpacing };
  },
}).configure({ levels: [1, 2, 3] });

export const PromptListItem = ListItem.extend({
  addAttributes() {
    return { ...this.parent?.(), markerGap };
  },
});

export const PromptBulletList = BulletList.extend({
  addAttributes() {
    return { ...this.parent?.(), ...blockSpacing };
  },
});

export const PromptOrderedList = OrderedList.extend({
  addAttributes() {
    return { ...this.parent?.(), ...blockSpacing };
  },
});

export const PromptBlockquote = Blockquote.extend({
  addAttributes() {
    return { ...this.parent?.(), ...blockSpacing };
  },
});

export const PROMPT_BLOCK_EXTENSIONS = [
  PromptParagraph,
  PromptHeading,
  PromptListItem,
  PromptBulletList,
  PromptOrderedList,
  PromptBlockquote,
];
